import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { EstimatedPicker, formatMinutes } from '@/features/tasks/public';
import { priorityFromInt, priorityToInt } from '@/shared/lib/priority';
import type { Priority } from '@/shared/lib/priority';
import { AI_PRIORITIES } from '../schemas';

export interface AiProposedTaskRowProps {
  index: number;
  title: string;
  priority: number | null;
  estimatedMinutes: number | null;
  estimateNote: string | null;
  selected: boolean;
  /** True when title is empty after edit — blocks confirmation. */
  titleError: boolean;
  onTitleChange: (value: string) => void;
  onPriorityChange: (value: number | null) => void;
  onEstimateChange: (minutes: number | null) => void;
  onSelectionChange: (selected: boolean) => void;
}

/**
 * The same tints the rest of the app gives a priority — a task that reads "Alta"
 * on the board reads "Alta" the same way here.
 */
const PRIORITY_TINT: Record<Priority, string> = {
  urgent: 'bg-chart-5/10 text-chart-5',
  high: 'bg-chart-3/15 text-chart-3',
  medium: 'bg-chart-4/10 text-chart-4',
  low: 'bg-chart-2/10 text-chart-2',
};

/**
 * One proposed task.
 *
 * Reads as a line — select it, see what it is, what it costs — and only turns into
 * controls when the user asks to edit it. Five rows of always-open selects is a form,
 * and a form is not something you scan.
 */
export function AiProposedTaskRow({
  index,
  title,
  priority,
  estimatedMinutes,
  estimateNote,
  selected,
  titleError,
  onTitleChange,
  onPriorityChange,
  onEstimateChange,
  onSelectionChange,
}: AiProposedTaskRowProps) {
  const { t } = useTranslation('ai');
  // An empty title must stay open: closing would hide the thing blocking confirmation.
  const [editing, setEditing] = useState(false);
  const isEditing = editing || titleError;

  const priorityValue = priorityFromInt(priority);

  return (
    <div
      data-testid={`ai-task-plan-row-${index}`}
      className={cn(
        'rounded-lg border px-3 py-2.5 transition-colors',
        selected ? 'border-border' : 'border-border/60',
        titleError && 'border-destructive',
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={selected}
          onCheckedChange={(checked) => onSelectionChange(checked === true)}
          data-testid={`ai-task-plan-row-${index}-checkbox`}
          aria-label={t('goalPanel.row.select', { title })}
          className="size-5 shrink-0 rounded-full"
        />

        {isEditing ? (
          <Input
            autoFocus
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && title.trim()) setEditing(false);
            }}
            data-testid={`ai-task-plan-row-${index}-title`}
            aria-label={t('goalPanel.row.title')}
            className={cn('h-8 flex-1 text-sm', titleError && 'border-destructive')}
          />
        ) : (
          <span
            data-testid={`ai-task-plan-row-${index}-title-text`}
            className={cn('min-w-0 flex-1 truncate text-sm', !selected && 'text-muted-foreground')}
          >
            {title}
          </span>
        )}

        {!isEditing && (
          <>
            {priorityValue && (
              <Badge
                variant="secondary"
                className={cn('shrink-0 rounded-md font-medium', PRIORITY_TINT[priorityValue])}
                data-testid={`ai-task-plan-row-${index}-priority-badge`}
              >
                {t(`quickCreate.priorities.${priorityValue}`)}
              </Badge>
            )}
            <span
              className="w-9 shrink-0 text-right text-xs text-muted-foreground"
              data-testid={`ai-task-plan-row-${index}-estimate-text`}
            >
              {estimatedMinutes == null ? '—' : formatMinutes(estimatedMinutes)}
            </span>
          </>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-muted-foreground"
              data-testid={`ai-task-plan-row-${index}-edit`}
              aria-label={isEditing ? t('goalPanel.row.doneEditing') : t('goalPanel.row.edit')}
              disabled={isEditing && titleError}
              onClick={() => setEditing((value) => !value)}
            >
              {isEditing ? <Check className="size-4" /> : <Pencil className="size-3.5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {isEditing ? t('goalPanel.row.doneEditing') : t('goalPanel.row.edit')}
          </TooltipContent>
        </Tooltip>
      </div>

      {isEditing && (
        <div className="mt-2.5 flex flex-wrap items-center gap-3 pl-8">
          <Select
            value={priorityValue ?? ''}
            onValueChange={(value) =>
              onPriorityChange(value ? priorityToInt(value as Priority) : null)
            }
          >
            <SelectTrigger
              className="h-8 w-[140px] text-xs"
              data-testid={`ai-task-plan-row-${index}-priority`}
            >
              <SelectValue placeholder={t('goalPanel.row.priority')} />
            </SelectTrigger>
            <SelectContent>
              {AI_PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {t(`quickCreate.priorities.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex h-8 items-center rounded-md border px-3 text-xs shadow-xs">
            <EstimatedPicker value={estimatedMinutes} onSave={onEstimateChange} />
          </div>
        </div>
      )}

      {estimateNote && estimatedMinutes == null && (
        <p
          data-testid={`ai-task-plan-row-${index}-estimate-note`}
          className="mt-1.5 pl-8 text-xs text-muted-foreground italic"
        >
          {t('goalPanel.estimateNote', { reason: estimateNote })}
        </p>
      )}
    </div>
  );
}
